import { useCallback, useMemo, useRef, useState } from 'react';

export interface Repeater<T> {
  rows: T[];
  /** A stable React key per row, parallel to `rows`. Survives removals. */
  keys: string[];
  add: () => void;
  remove: (index: number) => void;
  update: (index: number, changes: Partial<T>) => void;
  /** Replaces every row at once — a list filled from a suggestion, say. */
  replace: (rows: T[]) => void;
}

interface RepeaterState<T> {
  rows: T[];
  keys: string[];
}

/**
 * List state for a repeating sub-form — reference ranges, order lines, steps.
 *
 * Rows carry no server identity until the form is saved, so they are addressed
 * by index. Each also gets a client-only key, kept beside the rows rather than
 * in them so the submitted payload is exactly the rows: keying by index would
 * hand a removed row's input state to the row that slid into its place.
 *
 * Every update is immutable, and a form with several lists (the parameter form
 * has two) shares one implementation instead of writing it out per list.
 *
 * `blank` must be referentially stable — declare it at module scope.
 */
export function useRepeater<T extends object>(initial: T[], blank: T): Repeater<T> {
  const nextKey = useRef(0);
  const issueKeys = useCallback(
    (count: number) =>
      Array.from({ length: count }, () => {
        nextKey.current += 1;
        return `row-${nextKey.current}`;
      }),
    [],
  );

  const [state, setState] = useState<RepeaterState<T>>(() => ({
    rows: initial,
    keys: initial.map((_, index) => `initial-${index}`),
  }));

  const add = useCallback(
    () =>
      setState((current) => ({
        rows: [...current.rows, { ...blank }],
        keys: [...current.keys, ...issueKeys(1)],
      })),
    [blank, issueKeys],
  );

  const remove = useCallback(
    (index: number) =>
      setState((current) => ({
        rows: current.rows.filter((_, at) => at !== index),
        keys: current.keys.filter((_, at) => at !== index),
      })),
    [],
  );

  const update = useCallback(
    (index: number, changes: Partial<T>) =>
      setState((current) => ({
        ...current,
        rows: current.rows.map((row, at) => (at === index ? { ...row, ...changes } : row)),
      })),
    [],
  );

  const replace = useCallback(
    (rows: T[]) => setState({ rows, keys: issueKeys(rows.length) }),
    [issueKeys],
  );

  return useMemo(
    () => ({ rows: state.rows, keys: state.keys, add, remove, update, replace }),
    [state, add, remove, update, replace],
  );
}
